import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  accountService,
  categoryService,
  receiptService
} from '../../../apiServices';
import { useRefresh } from '../../../contexts/RefreshContext';
import {
  Account,
  Category,
  Receipt,
  CreatePositionRequest
} from '../../../apiServices/types';
import { formatCurrency } from '../../../utils';
import {
  ChartIcon
} from '../../UI/Icons';
import { GlassSelect } from '../../UI';
import './IncomePage.css';

const IncomePage: React.FC = () => {
  const [accounts, setAccounts] = useState<Account[]>([]);
  const [incomes] = useState<Receipt[]>([]); 
  const [categories, setCategories] = useState<Category[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string>('');
  const [editingIncomeId, setEditingIncomeId] = useState<string>('');
  const formRef = useRef<HTMLFormElement>(null);
  const [selectedAccountId, setSelectedAccountId] = useState<string>('');
  const [selectedCategory, setSelectedCategory] = useState<string>('salary');

  useEffect(() => {
    loadInitialData();
  }, []);

  const loadInitialData = async (): Promise<void> => {
    try {
      setLoading(true);
      await Promise.all([
        loadAccounts(),
        loadCategories()
      ]);
    } catch (error) {
    } finally {
      setLoading(false);
    }
  };

  const loadAccounts = async (): Promise<void> => {
    try {
      const data = await accountService.getAll();
      setAccounts(data);

      if (!selectedAccountId && data.length > 0) {
        const defaultAccount = data.find(account => account.isDefault);
        if (defaultAccount) {
          setSelectedAccountId(defaultAccount.id);
        } else if (data.length > 0) {
          setSelectedAccountId(data[0].id);
        }
      }
    } catch (error) {
    }
  };

  const loadCategories = async (): Promise<void> => {
    try {
      const data = await categoryService.getAll();
      setCategories(data);
    } catch (error) {
    }
  };

  const handleRefresh = useCallback(async () => {
    await Promise.all([loadAccounts(), loadCategories()]);
  }, []);

  useRefresh(handleRefresh);

  const handleIncomeSubmit = async (event: React.FormEvent<HTMLFormElement>): Promise<void> => {
    event.preventDefault();
    const formData = new FormData(event.currentTarget);

    const name = formData.get('income-name') as string;
    const amount = parseFloat(formData.get('income-amount') as string);
    const accountId = formData.get('income-account') as string;
    const categorySelect = formData.get('income-category') as string;
    const description = formData.get('income-description') as string;

    if (amount <= 0) {
      setError('Сума має бути більше 0');
      return;
    }
    if (!accountId) {
      setError('Оберіть рахунок');
      return;
    }

    setIsLoading(true);
    setError('');

    try {
      if (editingIncomeId) {
        await receiptService.update(editingIncomeId, { name });
        setEditingIncomeId('');
      } else {
        const receipt = await receiptService.create({
          name,
          accountId: accountId || '',
          type: 1 
        });

        const categoryName = getCategoryNameByValue(categorySelect) || 'Дохід';
        let categoryId = await findOrCreateIncomeCategory(categoryName);

        const positionData: CreatePositionRequest = {
          name: description || name,
          amount: amount,
          isBought: true 
        };
        if (categoryId) {
          positionData.categoryId = categoryId as string;
        }
        await receiptService.addPosition(receipt.id, positionData);

        await receiptService.submit(receipt.id);
      }

      formRef.current?.reset();
      const defaultAccount = accounts.find(account => account.isDefault);
      if (defaultAccount) {
        setSelectedAccountId(defaultAccount.id);
      } else {
        setSelectedAccountId('');
      }
      setSelectedCategory('salary');

      await loadAccounts(); 

      showSuccessMessage('Дохід успішно додано!');
    } catch (error) {
      setError('Помилка: ' + (error as Error).message);
    } finally {
      setIsLoading(false);
    }
  };

  const getCategoryNameByValue = (value: string): string => {
    const categories: Record<string, string> = {
      'salary': 'Зарплата',
      'freelance': 'Фриланс',
      'investment': 'Інвестиції',
      'sale': 'Продаж',
      'refund': 'Повернення',
      'cashback': 'Кешбек',
      'gift': 'Подарунок',
      'bonus': 'Бонус',
      'other': 'Інше'
    };
    return categories[value] || '';
  };

  const findOrCreateIncomeCategory = async (categoryName: string): Promise<string | undefined> => {
    try {
      const existingCategory = categories.find(c => c.name === categoryName);

      if (existingCategory) {
        return existingCategory.id;
      }

      const newCategory = await categoryService.create({ name: categoryName });
      setCategories(prev => [...prev, newCategory]);
      return newCategory.id;
    } catch (error) {
      return undefined;
    }
  };

  const showSuccessMessage = (message: string): void => {
    const successDiv = document.createElement('div');
    successDiv.className = 'alert alert-success';
    successDiv.textContent = message;
    successDiv.style.position = 'fixed';
    successDiv.style.top = '20px';
    successDiv.style.right = '20px';
    successDiv.style.zIndex = '9999';


    document.body.appendChild(successDiv);

    setTimeout(() => {

      setTimeout(() => {
        if (successDiv.parentNode) {
          successDiv.parentNode.removeChild(successDiv);
        }
      }, 300);
    }, 3000);
  };

  const updateIncomeStatistics = (): { totalAmount: number; count: number; avgAmount: number } => {
    const completedIncomes = incomes.filter(i => i.status === 1);
    const totalAmount = completedIncomes.reduce((sum, income) => sum + income.totalAmount, 0);
    const avgAmount = completedIncomes.length > 0 ? totalAmount / completedIncomes.length : 0;

    return {
      totalAmount,
      count: completedIncomes.length,
      avgAmount
    };
  };

  const { totalAmount, count, avgAmount } = updateIncomeStatistics();

  if (loading) {
    return (
      <div className="income-page">
        <div className="card">
          <div className="loading-spinner">
            <div className="spinner"></div>
            <p>Завантаження доходів...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="income-page">
      <div className="income-page-header">
        <div className="income-page-header-content">
          <h2 className="income-page-header-title">Додати дохід</h2>
          <p className="income-page-header-subtitle">Додайте новий дохід до свого рахунку</p>
        </div>
      </div>

      <div className="income-create-form-card">
        <form ref={formRef} onSubmit={handleIncomeSubmit} className="income-create-form" autoComplete="off" noValidate>
          <input type="hidden" name="income-id" value={editingIncomeId} />

          <div className="form-row">
            <div className="form-group">
              <label className="form-label" htmlFor="income-name">Назва</label>
              <input
                type="text"
                id="income-name"
                name="income-name"
                className="form-input"
                placeholder="Наприклад: Зарплата"
                autoComplete="off"
                required
              />
            </div>

            <div className="form-group">
              <label className="form-label" htmlFor="income-amount">Сума</label>
              <input
                type="number"
                id="income-amount"
                name="income-amount"
                className="form-input"
                placeholder="0.00"
                step="0.01"
                min="0"
                autoComplete="off"
                required
              />
            </div>
          </div>

          <div className="form-group">
            <label className="form-label" htmlFor="income-category">Категорія</label>
            <GlassSelect
              options={[
                { value: 'salary', label: 'Зарплата' },
                { value: 'investment', label: 'Інвестиції' },
                { value: 'sale', label: 'Продаж' },
                { value: 'refund', label: 'Повернення' },
                { value: 'cashback', label: 'Кешбек' },
                { value: 'gift', label: 'Подарунок' },
                { value: 'bonus', label: 'Бонус' },
                { value: 'other', label: 'Інше' }
              ]}
              value={selectedCategory}
              onChange={setSelectedCategory}
              placeholder="Оберіть категорію"
              searchable
            />
            <input type="hidden" name="income-category" value={selectedCategory} />
          </div>

          <div className="form-group">
            <label className="form-label" htmlFor="income-account">Рахунок</label>
            <GlassSelect
              options={accounts.map(account => ({
                value: account.id,
                label: `${account.name} ${account.isDefault ? '(Основний)' : ''}`.trim()
              }))}
              value={selectedAccountId}
              onChange={setSelectedAccountId}
              placeholder="Оберіть рахунок"
              searchable
            />
            <input type="hidden" name="income-account" value={selectedAccountId} />
          </div>

          {error && <div className="alert alert-error show">{error}</div>}

          <div className="income-form-actions">
            <button type="submit" className="income-create-button" disabled={isLoading}>
              {isLoading ? 'Збереження...' : (
                <>
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor"
                    strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M12 2v20M2 12h20"></path>
                  </svg>
                  <span>{editingIncomeId ? 'Оновити дохід' : 'Додати дохід'}</span>
                </>
              )}
            </button>
            {editingIncomeId && (
              <button
                type="button"
                className="income-cancel-button"
                onClick={() => setEditingIncomeId('')}
              >
                Скасувати
              </button>
            )}
          </div>
        </form>
      </div>

      {incomes.length > 0 && (
        <div className="card">
          <div className="card-header">
            <h3 className="card-title">
              <ChartIcon size={20} className="card-title-icon" />
              Підсумок доходів
            </h3>
          </div>
          <div className="stats-grid">
            <div className="stat-item">
              <div className="stat-value">{formatCurrency(totalAmount)}</div>
              <div className="stat-label">Всього доходів</div>
            </div>
            <div className="stat-item">
              <div className="stat-value">{count}</div>
              <div className="stat-label">Записів</div>
            </div>
            <div className="stat-item">
              <div className="stat-value">{formatCurrency(avgAmount)}</div>
              <div className="stat-label">Середній дохід</div>
            </div>
          </div>
        </div>
      )}


    </div>
  );
};

export default IncomePage;
